/**
 * FnoScoreBadge
 * Compact badge shown on each alert row in Dashboard (Recent/Parked) and Alerts page.
 * Auto-computes FNO score on mount. Shows a refresh icon to recompute on demand.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Box, Chip, Typography, Tooltip, CircularProgress, IconButton } from '@mui/material';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { upsertAssessment } from '../../store/fnoSlice';
import fnoService, { FnoAssessment } from '../../services/fno.service';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Props {
  alertId: number;
  symbol: string;
  direction?: 'LONG' | 'SHORT';
  assessment?: FnoAssessment;
}

/* Audit A-020: every badge without an assessment used to POST /fno/assess-alert on mount —
 * ~55 compute requests each time the Dashboard opened. Mounts in the same tick are now
 * coalesced into ONE /fno/assessments/batch lookup; only the alerts that still have no
 * stored assessment are scored, at most two at a time. */
let batchIds = new Set<number>();
let batchWaiters = new Map<number, Array<(a: FnoAssessment | null) => void>>();
let batchTimer: number | null = null;
function lookupStored(alertId: number): Promise<FnoAssessment | null> {
  return new Promise(resolve => {
    batchIds.add(alertId);
    const w = batchWaiters.get(alertId) || []; w.push(resolve); batchWaiters.set(alertId, w);
    if (batchTimer != null) return;
    batchTimer = window.setTimeout(async () => {
      const ids = Array.from(batchIds); const waiters = batchWaiters;
      batchIds = new Set(); batchWaiters = new Map(); batchTimer = null;
      let found: Record<number, FnoAssessment> = {};
      try { found = (await fnoService.getAssessmentsBatch(ids)) || {}; } catch { /* fall through to scoring */ }
      ids.forEach(id => (waiters.get(id) || []).forEach(r => r(found[id] || null)));
    }, 60);
  });
}
const SCORE_CONCURRENCY = 2;
let scoringNow = 0;
const scoreQueue: Array<() => void> = [];
function withScoreSlot<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const run = () => { scoringNow++; fn().then(resolve, reject).finally(() => { scoringNow--; const next = scoreQueue.shift(); if (next) next(); }); };
    if (scoringNow < SCORE_CONCURRENCY) run(); else scoreQueue.push(run);
  });
}

const FnoScoreBadge: React.FC<Props> = ({ alertId, symbol, direction = 'LONG', assessment }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { C } = useAppTheme();
  const [scoring, setScoring] = useState(false);
  const [notTracked, setNotTracked] = useState(false);
  const autoTriggered = useRef(false);

  const regimeColor = (regime: string) =>
    regime === 'STRONG' ? C.bull : regime === 'REVIEW' ? C.warn : C.bear;

  const doScore = async (auto = false) => {
    if (scoring) return;
    setScoring(true);
    try {
      if (auto) {
        const stored = await lookupStored(alertId);
        if (stored) { dispatch(upsertAssessment(stored)); return; }
      }
      const result = await (auto
        ? withScoreSlot(() => fnoService.assessAlert(alertId, symbol, direction))
        : fnoService.assessAlert(alertId, symbol, direction));
      dispatch(upsertAssessment(result));
      setNotTracked(false); // clear any prior not-tracked state on successful recompute
    } catch (err: any) {
      // 404 = symbol not tracked by FNO module — silently hide the badge
      const status = err?.response?.status;
      if (status === 404) {
        setNotTracked(true);
      }
      // Other errors: leave badge in current state (don't permanently hide)
    } finally {
      setScoring(false);
    }
  };

  // Auto-compute FNO score on mount (if no assessment yet and not already tried)
  useEffect(() => {
    if (!assessment && !autoTriggered.current && !notTracked) {
      autoTriggered.current = true;
      doScore(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScore = (e: React.MouseEvent) => {
    e.stopPropagation();
    doScore();
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    doScore();
  };

  // Symbol not tracked by FNO module — show nothing
  if (notTracked) return null;

  // Not yet scored
  if (!assessment) {
    return (
      <Tooltip title={scoring ? 'Computing FNO score…' : 'Click to compute FNO score'} placement="top" arrow>
        <Chip
          size="small"
          icon={
            scoring
              ? <CircularProgress size={10} sx={{ color: `${C.dim} !important` }} />
              : <CandlestickChartIcon sx={{ fontSize: '0.7rem !important', color: `${C.dim} !important` }} />
          }
          label={
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: C.dim, fontWeight: 600 }}>
              {scoring ? 'FNO…' : 'FNO?'}
            </Typography>
          }
          onClick={handleScore}
          sx={{
            bgcolor: C.dimMuted,
            border: `1px dashed ${C.dim}55`,
            height: 20,
            cursor: scoring ? 'default' : 'pointer',
            '&:hover': { bgcolor: scoring ? undefined : C.dimMuted }
          }}
        />
      </Tooltip>
    );
  }

  const regime = assessment.regime;
  const color  = regimeColor(regime);
  const score  = Math.round(Number(assessment.combinedScore));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Tooltip title={`FNO Score: ${score} · ${regime} · ${assessment.summary || 'Click to open FNO Lab'}`} placement="top" arrow>
        <Chip
          size="small"
          icon={<CandlestickChartIcon sx={{ fontSize: '0.7rem !important', color: `${color} !important` }} />}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem', color }}>
                {score}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>
                FNO
              </Typography>
            </Box>
          }
          onClick={(e) => { e.stopPropagation(); navigate(`/options-lab/${assessment.symbol || symbol}`); }}
          sx={{
            // Transparent, like the other row badges. This sits on alert rows that already
            // carry a tone tint, so its own 9% fill composited on top and took the score to
            // 4.36:1. The border carries the regime colour; the hover fill is fine because a
            // pointer is on it and the text is not being read at that moment.
            bgcolor: 'transparent',
            border: `1px solid ${color}55`,
            height: 20,
            cursor: 'pointer',
            '&:hover': { bgcolor: `${color}30` }
          }}
        />
      </Tooltip>
      {/* Refresh icon — recompute score based on latest market data */}
      <Tooltip title="Recompute FNO score" placement="top" arrow>
        <span>
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={scoring}
            sx={{ p: 0.25, color: C.dim, '&:hover': { color } }}
          >
            {scoring
              ? <CircularProgress size={10} />
              : <RefreshIcon sx={{ fontSize: 12 }} />
            }
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default FnoScoreBadge;
